// src/components/landing/LandingPage.tsx
// OLDEST version — clean photo-background hero, simple structure.
// Ported from Next.js (next/router) to react-router-dom + new auth slide-over panels.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { safeRedirectPath } from '@/lib/redirect';
import { useAuth } from '@/contexts/AuthContext.tsx';
import {
    Building,
    GraduationCap,
    LayoutDashboard,
    LogIn,
    CheckCircle2,
    ArrowRight,
    X,
    Menu,
    Lock,
    ShieldCheck,
    Handshake,
    KeyRound,
} from 'lucide-react';

import { FeatureCard } from './FeatureCard.tsx';
import { CtaButton } from './CtaButton.tsx';
import { Partners } from './Partners.tsx';
import { ContactCTA } from './ContactCTA.tsx';
import { Footer } from './Footer.tsx';
import { ProfileJourneyModal } from '@/components/dashboard/candidate/ProfileJourneyModal.tsx';
import { LoginForm } from '../signin/LoginForm.tsx';
import { cn } from '@/lib/utils.ts';
import CONFIG from '@/lib/config';
import AppHeader from '@/components/layout/AppHeader';

type SlidePanel = 'none' | 'login' | 'employer' | 'candidate';

export const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();

    // Slide-over panel state (login / employer / candidate)
    const [activePanel, setActivePanel] = useState<SlidePanel>('none');
    /**
     * The address to seed the login form with, set when the signup panel hands
     * over. Held here rather than inside LoginForm because the form is
     * unmounted while the signup panel is open — state local to it would not
     * survive the swap.
     */
    const [loginPrefillEmail, setLoginPrefillEmail] = useState<string>('');

    // Header / mobile-menu state
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Refs for scroll-to sections
    const efficiencyRef = useRef<HTMLElement>(null);
    const welfareRef = useRef<HTMLElement>(null);

    // Track scroll for header shadow
    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close panel on Escape
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setActivePanel('none');
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, []);

    const scrollTo = (ref: React.RefObject<HTMLElement | null>) => {
        ref.current?.scrollIntoView({ behavior: 'smooth' });
        setMobileMenuOpen(false);
    };

    // Lock body scroll when a panel is open
    useEffect(() => {
        if (activePanel !== 'none') {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [activePanel]);

    // #265 — a shared public job link routes logged-out visitors here as
    // `/?redirect=/dashboard/jobs/:id`. Honour it (open-redirect-safe): an authed
    // visitor is sent straight on; a logged-out one gets the login panel opened for
    // them, and login success lands on the target instead of the generic dashboard.
    const redirectTarget = safeRedirectPath(searchParams.get('redirect'));

    useEffect(() => {
        if (!redirectTarget) return;
        if (user) {
            navigate(redirectTarget, { replace: true });
        } else {
            setActivePanel('login');
        }
    }, [redirectTarget, user, navigate]);

    const handleLoginSuccess = (success: boolean) => {
        if (success) {
            setActivePanel('none');
            navigate(redirectTarget ?? '/dashboard');
        }
    };

    return (
        <div className="flex overflow-hidden flex-col bg-neutral-100">
            {/* ==================== STICKY HEADER ==================== */}
            <AppHeader
                position="fixed"
                scrolled={scrolled}
                rightContent={
                    <>
                        {/* Desktop Nav */}
                        <nav className="hidden md:flex items-center gap-1 mr-2">
                            <button
                                onClick={() => scrollTo(efficiencyRef)}
                                className={cn(
                                    'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                                    scrolled ? 'text-[#212121] hover:text-[#212121] hover:bg-gray-50' : 'text-[#f5f5f5] hover:text-[#f5f5f5] hover:bg-[#f5f5f5]/10',
                                )}
                            >
                                Så fungerar det
                            </button>
                            <button
                                onClick={() => scrollTo(welfareRef)}
                                className={cn(
                                    'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                                    scrolled ? 'text-[#212121] hover:text-[#212121] hover:bg-gray-50' : 'text-[#f5f5f5] hover:text-[#f5f5f5] hover:bg-[#f5f5f5]/10',
                                )}
                            >
                                För arbetsgivare
                            </button>
                            <button
                                onClick={() => { setMobileMenuOpen(false); navigate('/om-oss'); }}
                                className={cn(
                                    'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                                    scrolled ? 'text-[#212121] hover:text-[#212121] hover:bg-gray-50' : 'text-[#f5f5f5] hover:text-[#f5f5f5] hover:bg-[#f5f5f5]/10',
                                )}
                            >
                                Om oss
                            </button>
                        </nav>

                        {/* Action buttons */}
                        <div className="hidden md:flex items-center gap-3">
                            {CONFIG.PUBLIC_APP_ENABLED && user && (
                                <button
                                    onClick={() => navigate('/dashboard')}
                                    className={cn(
                                        'h-[54px] inline-flex items-center justify-center gap-2 px-5 text-sm font-medium rounded-lg transition-colors',
                                        scrolled ? 'text-[#1A4D4F] border-2 border-[#1A4D4F] hover:bg-[#1A4D4F]/5' : 'text-[#f5f5f5] border-2 border-[#f5f5f5]/30 hover:bg-[#f5f5f5]/10',
                                    )}
                                >
                                    <LayoutDashboard className="w-4 h-4" />
                                    Dashboard
                                </button>
                            )}
                            {CONFIG.PUBLIC_APP_ENABLED && !user && (
                                <button
                                    onClick={() => setActivePanel('login')}
                                    className={cn(
                                        'w-[124px] h-[54px] inline-flex items-center justify-center text-sm font-medium rounded-lg transition-colors',
                                        scrolled ? 'text-[#1A4D4F] border-2 border-[#1A4D4F] hover:bg-[#1A4D4F]/5' : 'text-[#f5f5f5] border-2 border-[#f5f5f5]/30 hover:bg-[#f5f5f5]/10',
                                    )}
                                >
                                    Logga in
                                </button>
                            )}
                            {!user && (
                                <button
                                    onClick={() => setActivePanel('employer')}
                                    className="w-[124px] h-[54px] inline-flex items-center justify-center gap-1.5 bg-[#1A4D4F] text-[#f5f5f5] text-sm font-semibold rounded-lg hover:bg-[#1A4D4F]/90 transition-colors"
                                >
                                    <Building className="w-3.5 h-3.5" />
                                    Boka demo
                                </button>
                            )}
                        </div>

                        {/* Mobile menu button */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className={cn(
                                'md:hidden p-2 rounded-lg',
                                scrolled ? 'text-[#212121] hover:text-[#212121] hover:bg-gray-50' : 'text-[#f5f5f5] hover:bg-[#f5f5f5]/10',
                            )}
                        >
                            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </>
                }
            />

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="fixed top-16 sm:top-[72px] left-0 right-0 z-40 bg-white md:hidden border-b border-gray-100 py-3 space-y-1 px-4 sm:px-6 animate-in fade-in slide-in-from-top-2 duration-200">
                    <button
                        onClick={() => scrollTo(efficiencyRef)}
                        className="block w-full text-left px-4 py-2.5 text-sm font-medium text-[#212121] hover:bg-gray-50 rounded-lg"
                    >
                        Så fungerar det
                    </button>
                    <button
                        onClick={() => scrollTo(welfareRef)}
                        className="block w-full text-left px-4 py-2.5 text-sm font-medium text-[#212121] hover:bg-gray-50 rounded-lg"
                    >
                        För välfärden
                    </button>
                    <button
                        onClick={() => { setMobileMenuOpen(false); navigate('/om-oss'); }}
                        className="block w-full text-left px-4 py-2.5 text-sm font-medium text-[#212121] hover:bg-gray-50 rounded-lg"
                    >
                        Om oss
                    </button>
                    <div className="border-t border-gray-100 pt-3 mt-2 space-y-2 px-4">
                        {CONFIG.PUBLIC_APP_ENABLED && user && (
                            <button
                                onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-[#212121] border border-gray-200 rounded-lg hover:bg-gray-50"
                            >
                                <LayoutDashboard className="w-4 h-4" />
                                Dashboard
                            </button>
                        )}
                        {CONFIG.PUBLIC_APP_ENABLED && !user && (
                            <button
                                onClick={() => { setActivePanel('login'); setMobileMenuOpen(false); }}
                                className="w-full px-4 py-2.5 text-sm font-medium text-[#212121] border border-gray-200 rounded-lg hover:bg-gray-50"
                            >
                                Logga in
                            </button>
                        )}
                        {!user && (
                            <button
                                onClick={() => { setActivePanel('employer'); setMobileMenuOpen(false); }}
                                className="w-full flex items-center justify-center gap-1.5 px-5 py-2.5 bg-[#1A4D4F] text-[#f5f5f5] text-sm font-semibold rounded-lg"
                            >
                                <Building className="w-3.5 h-3.5" />
                                Boka demo
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* HERO SECTION */}
            <section
                className="relative flex flex-col overflow-hidden justify-center items-start px-6 sm:px-12 md:px-20 pt-[72px] min-h-screen w-full text-[#f5f5f5] bg-cover bg-no-repeat bg-center"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80)',
                }}
            >
                <div className="absolute inset-0 bg-[#1A4D4F] opacity-70"></div>

                <div className="relative z-10 flex flex-col max-w-full w-full md:w-[796px]">
                    <h1 className="text-4xl sm:text-5xl md:text-7xl font-medium leading-tight md:leading-[84px]">
                        Nya medarbetare behöver tid att bli trygga. Vi gör den tiden kortare.
                    </h1>
                    <p className="mt-6 text-lg sm:text-xl md:text-2xl leading-relaxed md:leading-9">
                        Nexilink ger din organisation digital onboarding och träning på verkliga situationer.
                        Nya medarbetare hittar sin roll snabbare och du kan fokusera på att leda.
                    </p>
                    <div className="flex flex-col sm:flex-row items-start gap-4 mt-10">
                        <CtaButton
                            label="Boka ett samtal"
                            icon={<ArrowRight className="w-6 h-6" />}
                            onClick={() => setActivePanel('employer')}
                        />
                    </div>
                </div>
            </section>

            {/* EFFICIENCY SECTION */}
            <section ref={efficiencyRef} className="flex flex-col justify-center px-6 sm:px-12 md:px-24 py-16 md:py-24 min-h-screen w-full bg-white scroll-mt-20">
                <div className="flex flex-col items-center text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#212121] leading-tight md:leading-[56px]">
                        Så här fungerar Nexilink
                    </h2>
                    <p className="mt-6 text-base sm:text-lg md:text-xl text-[#212121]/80 max-w-4xl">
                        Nya medarbetare behöver en trygg start. Chefer behöver tid att leda.
                        Nexilink löser båda på en gång.
                    </p>
                </div>

                {/* Feature Cards — 2 cards: Digital onboarding (left) + AI-driven training (right) */}
                <div className="flex flex-wrap justify-center gap-8">
                    <FeatureCard
                        image="https://cdn.builder.io/api/v1/image/assets/89bc6265b1a84995b7b85b1453a49e93/f25396d6785cba4fa4ff818b6954f0052ee7367a7eacdec60df8016697673349?apiKey=89bc6265b1a84995b7b85b1453a49e93&"
                        title="Digital onboarding och introduktion"
                        description="Er verksamhets rutiner, riktlinjer och introduktionsmaterial samlas digitalt på ett ställe. Nya medarbetare hittar vad de behöver själva och chefen slipper upprepa samma saker om och om igen."
                    />
                    <FeatureCard
                        image="https://cdn.builder.io/api/v1/image/assets/89bc6265b1a84995b7b85b1453a49e93/70cfde0357f6303f0c477e7da1d8cd7c16c05c082b8a24765af9a31a1e89a788?apiKey=89bc6265b1a84995b7b85b1453a49e93&"
                        title="Kompetensträning som förbereder på riktigt"
                        description="Innan en ny medarbetare möter skarpa situationer har de redan tränat på dem. Genom realistiska fall lär de sig ert arbetssätt i sin egen takt och kommer in i rollen mycket snabbare."
                    />
                </div>
            </section>

            {/* FOR WELFARE SECTION */}
            <section ref={welfareRef} className="flex flex-col justify-center px-6 sm:px-12 md:px-24 py-16 md:py-24 min-h-screen w-full bg-[#A4C3B2] text-[#212121] scroll-mt-20">
                <div className="flex gap-16 lg:gap-24 justify-between items-center w-full max-lg:flex-col">
                    {/* Content */}
                    <div className="flex flex-col flex-1 max-w-[592px]">
                        <div className="text-xl font-medium text-teal-900">För arbetsgivare</div>
                        <h2 className="mt-4 text-4xl md:text-5xl font-semibold leading-tight md:leading-[56px]">
                            Dina nya medarbetare förtjänar en bättre start
                        </h2>
                        <p className="mt-6 text-lg leading-relaxed">
                            I pressade verksamheter är det lätt att introduktionen prioriteras ner.
                            Chefen har inte tid, kollegorna är redan överbelastade och den nya medarbetaren
                            lämnas att hitta sin egen väg. Det märks snabbt. På kvaliteten, på trivseln
                            och på viljan att stanna kvar.
                        </p>

                        <div className="flex flex-col gap-6 mt-12">
                            <ul className="space-y-3">
                                <li className="flex gap-3 items-start">
                                    <CheckCircle2 className="h-5 w-5 text-teal-900 flex-shrink-0 mt-0.5" />
                                    <span><strong>Strukturerad från dag ett</strong> - allt introduktionsmaterial på ett ställe</span>
                                </li>
                                <li className="flex gap-3 items-start">
                                    <CheckCircle2 className="h-5 w-5 text-teal-900 flex-shrink-0 mt-0.5" />
                                    <span><strong>Anpassat efter er verksamhet</strong> och era rutiner</span>
                                </li>
                                <li className="flex gap-3 items-start">
                                    <CheckCircle2 className="h-5 w-5 text-teal-900 flex-shrink-0 mt-0.5" />
                                    <span><strong>Medarbetaren tränar</strong> på verkliga situationer i sin egen takt</span>
                                </li>
                                <li className="flex gap-3 items-start">
                                    <CheckCircle2 className="h-5 w-5 text-teal-900 flex-shrink-0 mt-0.5" />
                                    <span><strong>Chefen kan fokusera på att leda</strong> istället för att lära upp</span>
                                </li>
                            </ul>

                            <div className="mt-4">
                                <CtaButton
                                    label="Boka ett samtal"
                                    icon={<ArrowRight className="w-6 h-6" />}
                                    onClick={() => setActivePanel('employer')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Image */}
                    <div className="flex-1 max-w-[592px]">
                        <img
                            loading="lazy"
                            src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80"
                            alt="Välfärden"
                            className="w-full object-cover rounded-2xl shadow-lg aspect-[1.23]"
                        />
                    </div>
                </div>
            </section>

            {/* PARTNERS */}
            <Partners />

            {/* DATA SECURITY */}
            <section className="flex flex-col justify-center px-6 sm:px-12 md:px-24 py-16 md:py-24 min-h-screen w-full bg-neutral-100 text-[#212121]">
                <div className="max-w-6xl mx-auto w-full">
                    {/* Heading */}
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight md:leading-[56px] mb-6">
                            Er data är trygg hos oss
                        </h2>
                        <p className="text-base sm:text-lg md:text-xl text-[#212121] leading-relaxed max-w-3xl mx-auto">
                            Vi vet att kommuner hanterar känslig information och att kraven på säkerhet är höga.
                            Därför har vi byggt Nexilink med dataskydd som en grundförutsättning, inte som en eftertanke.
                        </p>
                    </div>

                    {/* 4 columns */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
                        {/* Lokal datahantering */}
                        <div className="flex flex-col items-start">
                            <div className="h-20 flex items-end mb-5">
                                <Lock className="w-16 h-16 text-[#212121] stroke-[1.2]" />
                            </div>
                            <h3 className="text-lg font-bold text-[#212121] mb-2">Lokal datahantering</h3>
                            <p className="text-[#212121]/60 leading-relaxed text-sm">
                                Databas, filer och dokument lagras på europeiska servrar via Scaleway i Amsterdam.
                                E-post via Brevo med EU-datacenter.
                            </p>
                        </div>

                        {/* Vi följer GDPR */}
                        <div className="flex flex-col items-start">
                            <div className="h-20 flex items-end mb-5">
                                <ShieldCheck className="w-16 h-16 text-[#212121] stroke-[1.2]" />
                            </div>
                            <h3 className="text-lg font-bold text-[#212121] mb-2">Vi följer GDPR</h3>
                            <p className="text-[#212121]/60 leading-relaxed text-sm">
                                Vi erbjuder personuppgiftsbiträdesavtal (DPA) som reglerar hur er data hanteras, lagras och skyddas.
                            </p>
                        </div>

                        {/* Er data tillhör er */}
                        <div className="flex flex-col items-start">
                            <div className="h-20 flex items-end mb-5">
                                <Handshake className="w-16 h-16 text-[#212121] stroke-[1.2]" />
                            </div>
                            <h3 className="text-lg font-bold text-[#212121] mb-2">Er data tillhör er</h3>
                            <p className="text-[#212121]/60 leading-relaxed text-sm">
                                Ni bestämmer vad som delas. Vi använder aldrig er data för ändamål ni inte godkänt.
                            </p>
                        </div>

                        {/* Google-inloggning */}
                        <div className="flex flex-col items-start">
                            <div className="h-20 flex items-end mb-5">
                                <KeyRound className="w-16 h-16 text-[#212121] stroke-[1.2]" />
                            </div>
                            <h3 className="text-lg font-bold text-[#212121] mb-2">Google-inloggning</h3>
                            <p className="text-[#212121]/60 leading-relaxed text-sm">
                                E-post delas med Google enligt deras policy. Du kan alltid välja e-post och lösenord istället.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CONTACT CTA */}
            <section className="flex flex-col px-6 sm:px-12 md:px-24 py-16 md:py-24 w-full bg-[#d2e1d9]">
                <ContactCTA />
            </section>

            {/* FOOTER */}
            <Footer />

            {/* ==================== SLIDE-OVER PANELS (login / employer / candidate) ==================== */}

            {/* Backdrop */}
            {activePanel !== 'none' && (
                <div
                    className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity"
                    onClick={() => setActivePanel('none')}
                />
            )}

            {/* Panel container */}
            <div
                className={cn(
                    // Island slide-over: floating, inset on all sides, rounded + bordered
                    // to match the app's island modals. Keeps its own slide animation.
                    'fixed top-3 bottom-3 right-3 z-50 w-full sm:w-[560px] lg:w-[640px] max-w-[calc(100vw-1.5rem)] bg-white rounded-2xl border-2 border-gray-100 shadow-2xl transition-transform duration-300 ease-out flex flex-col overflow-hidden',
                    activePanel !== 'none' ? 'translate-x-0' : 'translate-x-[calc(100%+0.75rem)]'
                )}
            >
                {/* Panel header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        {activePanel === 'login' && (
                            <>
                                <div className="w-9 h-9 bg-[#A4C3B2] rounded-lg flex items-center justify-center">
                                    <LogIn className="w-5 h-5 text-[#1A4D4F]" />
                                </div>
                                <div>
                                    <h2 className="font-bold text-gray-900">Logga in</h2>
                                    <p className="text-xs text-gray-500">Välkommen tillbaka</p>
                                </div>
                            </>
                        )}
                        {activePanel === 'employer' && (
                            <>
                                <div className="w-9 h-9 bg-[#A4C3B2] rounded-lg flex items-center justify-center">
                                    <Building className="w-5 h-5 text-[#1A4D4F]" />
                                </div>
                                <div>
                                    <h2 className="font-bold text-gray-900">För arbetsgivare</h2>
                                    <p className="text-xs text-gray-500">Kontakta oss för att komma igång</p>
                                </div>
                            </>
                        )}
                        {activePanel === 'candidate' && (
                            <>
                                <div className="w-9 h-9 bg-[#A4C3B2] rounded-lg flex items-center justify-center">
                                    <GraduationCap className="w-5 h-5 text-[#1A4D4F]" />
                                </div>
                                <div>
                                    <h2 className="font-bold text-gray-900">Skapa kandidatprofil</h2>
                                    <p className="text-xs text-gray-500">Det tar bara 2 minuter</p>
                                </div>
                            </>
                        )}
                    </div>

                    <button
                        onClick={() => setActivePanel('none')}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Panel content */}
                <div className="flex-1 overflow-y-auto">
                    {activePanel === 'login' && (
                        <LoginForm
                            onLoginCallback={handleLoginSuccess}
                            onRegisterClick={() => setActivePanel('candidate')}
                            mode="embedded"
                            prefillEmail={loginPrefillEmail}
                            /* key: LoginForm seeds its email state in the
                               initialiser, so it has to remount for a newly
                               arriving address to take. Without this the field
                               would stay empty on the swap from signup. */
                            key={loginPrefillEmail || 'login'}
                        />
                    )}
                    {activePanel === 'employer' && (
                        <ContactCTA mode="embedded" />
                    )}
                    {activePanel === 'candidate' && (
                        <ProfileJourneyModal
                            isOpen={true}
                            onClose={() => setActivePanel('none')}
                            onSwitchToLogin={(email) => {
                                if (email) setLoginPrefillEmail(email);
                                setActivePanel('login');
                            }}
                            mode="embedded"
                        />
                    )}
                </div>

                {/* Panel footer — switch between forms */}
                {activePanel === 'login' && !user && (
                    <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4 bg-gray-50">
                        <p className="text-sm text-center text-gray-600">
                            Inget konto?{' '}
                            <button
                                onClick={() => setActivePanel('candidate')}
                                className="text-[#1A4D4F] font-semibold hover:text-[#1A4D4F]/80 hover:underline"
                            >
                                Registrera dig som kandidat
                            </button>
                            {' '}eller{' '}
                            <button
                                onClick={() => setActivePanel('employer')}
                                className="text-[#1A4D4F] font-semibold hover:text-[#1A4D4F]/80 hover:underline"
                            >
                                kontakta oss som arbetsgivare
                            </button>
                        </p>
                    </div>
                )}
                {activePanel === 'candidate' && !user && (
                    <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4 bg-gray-50">
                        <p className="text-sm text-center text-gray-600">
                            Har du redan ett konto?{' '}
                            <button
                                onClick={() => setActivePanel('login')}
                                className="text-[#1A4D4F] font-semibold hover:text-[#1A4D4F]/80 hover:underline"
                            >
                                Logga in
                            </button>
                        </p>
                    </div>
                )}
                {activePanel === 'employer' && CONFIG.PUBLIC_APP_ENABLED && (
                    <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4 bg-gray-50">
                        <p className="text-sm text-center text-gray-600">
                            Är du kandidat?{' '}
                            <button
                                onClick={() => setActivePanel('candidate')}
                                className="text-[#1A4D4F] font-semibold hover:text-[#1A4D4F]/80 hover:underline"
                            >
                                Skapa en kandidatprofil
                            </button>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};
